import * as React from "react";
import { useUser } from "../../AuthContext";
import { cancelSubscription } from "../../api/PricingAPI";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid2 from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const tiers = [
  {
    title: 'Free',
    price: '0',
    description: [
      '50 Tweets only',
      'Basic AI for tweets',
      'Basic Email support'
    ],
    buttonText: 'Sign up for free',
    buttonVariant: 'outlined',
    buttonColor: 'primary',
  },
  {
    title: 'Premium',
    subheader: 'Recommended',
    price: '5',
    description: [
      'Unlimited Tweets',
      'Smart tweets powered by reasoning AI',
      'Priority email support'
    ],
    buttonText: 'Get Premium',
    buttonVariant: 'contained',
    buttonColor: 'primary',
  }
];

export default function Pricing() {
  const { user, isPaidUser, subscriptionId, clientId, accessToken } = useUser();
  const [openConfirm, setOpenConfirm] = React.useState(false);

  const handleUpgrade = () => {
    window.location.href = '/payment';
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const confirmCancelSubscription = async () => {
    await cancelSubscription(subscriptionId, clientId, accessToken);
    setOpenConfirm(false);
    window.location.reload();
  };

  return (
    <>
      <Grid2 container spacing={3} sx={{ alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%' }}>
        {tiers.map((tier) => {
          const isCurrentPlan = (isPaidUser && tier.title === 'Premium') || (!isPaidUser && tier.title === 'Free');
          const isFreePlan = tier.title === 'Free';
          const isPremiumPlan = tier.title === 'Premium';
          
          const handleButtonClick = () => {
            if (isPremiumPlan && !isPaidUser) {
              handleUpgrade();
            } else if (isPaidUser && isFreePlan) {
              handleOpenConfirm();
            }
          };

          return (
            <Grid2 item xs={12} sm={6} md={4} key={tier.title}>
              <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <CardContent>
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Typography component="h3" variant="h6">
                      {tier.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography component="h6" variant="h6">
                      ${tier.price} per month
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2, opacity: 0.8, borderColor: 'divider' }} />
                  {tier.description.map((line) => (
                    <Box key={line} sx={{ py: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <CheckCircleRoundedIcon sx={{ width: 20 }} />
                      <Typography variant="subtitle2" component={'span'}>{line}</Typography>
                    </Box>
                  ))}
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth 
                    variant={tier.buttonVariant} 
                    color={isCurrentPlan ? "none" : tier.buttonColor} 
                    disabled={(isPaidUser && isPremiumPlan) || (!isPaidUser && isFreePlan)}
                    onClick={handleButtonClick}
                  >
                    {isPaidUser && isFreePlan ? 'Cancel Subscription' : 
                     isCurrentPlan ? 'Current Plan' : 
                     tier.buttonText}
                  </Button>
                </CardActions>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>
      
      {/* Confirmation Modal */}
      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to cancel your subscription?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            No
          </Button>
          <Button onClick={confirmCancelSubscription} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
